import { observable, action, computed, toJS } from 'mobx'
import lodash from 'lodash'
import { ICompany, IRelationship, INode } from './graph/types'
import GNode from './graph/node'
import GLink from './graph/link'
import forceSimUtil from '../utils/forceSimUtil'
import ToolBarStore from './graph/ToolBarStore'
import { makeLayoutData } from './graph/layout'

class GraphStore {
    public toolBarStore = new ToolBarStore()

    // 搜索相关
    @observable public searchText: string = ''
    @observable public governingLaw: string = ''
    @observable public counterparty: string = ''
    @observable public language: string = ''
    @observable public disputeResolution: string = ''
    @observable public contractType: number = 0
    @observable public isConfidentiality: boolean = false
    @observable public noConfidentiality: boolean = false
    @observable public isChangeOfControl: boolean = false
    @observable public noChangeOfControl: boolean = false
    @observable public isIndemnity: boolean = false
    @observable public noIndemnity: boolean = false
    @observable public sensitivity: string = ''
    @observable public current: boolean = false
    @observable public historical: boolean = false
    @observable public date_sign_start: string = ''
    @observable public date_sign_end: string = ''
    @observable public termination_start: string = ''
    @observable public termination_end: string = ''
    @observable public contract_sum_start: string = ''
    @observable public contract_sum_end: string = ''
    @observable public isOpenEnded: boolean = false
    @observable public noOpenEnded: boolean = false

    // 业务相关
    @observable public ids: number[] = []
    @observable public entities: ICompany[] = []
    @observable public matchEntitys: ICompany[] = []
    @observable public loading: boolean = false
    @observable public width: number = 1400
    @observable public height: number = 900
    @observable public latestSelectedNode?: GNode
    @observable public latestSelectedLink?: GLink
    @observable public contracts: any[] = []
    @observable public sum: number = 0
    public force: any

    @observable private _nodes: GNode[] = []
    @observable private _links: GLink[] = []

    @computed
    public get confidentiality() {
        if ((this.isConfidentiality && this.noConfidentiality)
            || (!this.isConfidentiality && !this.noConfidentiality)) {
            return ''
        } else {
            return this.isConfidentiality ? 'yes' : 'no'
        }
    }

    @computed
    public get changeOfControl() {
        if ((this.isChangeOfControl && this.noChangeOfControl)
            || (!this.isChangeOfControl && !this.noChangeOfControl)) {
            return ''
        } else {
            return this.isChangeOfControl ? 'yes' : 'no'
        }
    }

    @computed
    public get indemnity() {
        if ((this.isIndemnity && this.noIndemnity)
            || (!this.isIndemnity && !this.noIndemnity)) {
            return ''
        } else {
            return this.isIndemnity ? 'yes' : 'no'
        }
    }

    /**
     * 聚合关系
     *
     * @readonly
     * @memberof GraphStore
     */
    @computed public get links() {
        this._links.forEach(link => {
            link.aggregationProps.clear()
        })
        const links = this._links.filter(link => {
            const source = link.source.id
            const target = link.target.id
            return this.nodesMap[source] && this.nodesMap[target]
        })
        const filterLinks = links.filter(link => {
            if (link.type === 'Own') {
                return true
            } else {
                return this.ids.includes(link.props.id)
            }
        })
        const linkMap = new Map<string, GLink>()
        filterLinks.forEach(link => {
            if (link.id.startsWith('Own-') || link.id.startsWith('Contract-')) {
                linkMap.set(link.id, link)
            }
        })
        for (const link of filterLinks) {
            const key = `${link.type}-${link.source.id}-${link.target.id}`
            let aggreationLink = linkMap.get(key)
            if (aggreationLink === undefined) {
                aggreationLink = link
                linkMap.set(key, aggreationLink)
                aggreationLink.id = key
            }
            const props = link.props
            aggreationLink.aggregationProps.set(props.id, props)
        }
        const aggreationLinks = Array.from(linkMap.values())
        return aggreationLinks
    }

    /**
     * 解压聚合关系
     * @readonly
     * @memberof GraphStore
     */
    @computed public get flatLinks() {
        const links = []
        for (const link of this._links) {
            const flat_link = lodash.cloneDeep(link)
            flat_link.id = `${flat_link.props.relId}`
            flat_link.aggregationProps.clear()
            links.push(flat_link)
        }
        return links
    }

    @computed public get nodes() {
        const links = this.links
        const ids = []
        const add_ids = this.entities.map(item => item.id)
        links.forEach(link => {
            ids.push(link.source.id, link.target.id)
        })
        const nodes = this._nodes.filter(node => {
            return add_ids.includes(parseInt(node.id, 10)) || ids.includes(node.id)
        })
        return nodes
    }

    @computed public get nodesMap() {
        return lodash.keyBy(this._nodes, item => item.id)
    }

    @computed public get linksMap() {
        const linksMap = new Map()
        this._links.forEach(link => {
            let key = ''
            if (link.type === 'Own') {
                key = `${link.type}-${link.source.id}-${link.target.id}`
            } else {
                key = `${link.type}-${link.source.id}-${link.target.id}-${link.props.id}`
            }
            linksMap.set(key, link)
        })
        return linksMap
    }

    @computed public get partbGroupIds() {
        const partbGroupIds = this.nodes.filter(item => item.props.group === true && !item.props.hkex_group)
            .map(item => parseInt(item.id, 10))
        return partbGroupIds
    }

    @computed
    public get open_ended() {
        if ((this.isOpenEnded && this.noOpenEnded)
            || (!this.isOpenEnded && !this.noOpenEnded)) {
            return ''
        } else {
            return this.isOpenEnded ? 'yes' : 'no'
        }
    }

    @action
    public clear() {
        this.toolBarStore.clear()
        this.searchText = ''
        this.governingLaw = ''
        this.counterparty = ''
        this.language = ''
        this.disputeResolution = ''
        this.contractType = 0
        this.isConfidentiality = false
        this.noConfidentiality = false
        this.isChangeOfControl = false
        this.noChangeOfControl = false
        this.isIndemnity = false
        this.noIndemnity = false
        this.sensitivity = ''
        this.current = false
        this.historical = false
        this.date_sign_start = ''
        this.date_sign_end = ''
        this.termination_start = ''
        this.termination_end = ''
        this.contract_sum_start = ''
        this.contract_sum_end = ''
        this.isOpenEnded = false
        this.noOpenEnded = false

        // 业务相关
        this.ids = []
        this.entities = []
        this.matchEntitys = []
        this.loading = false
        this.width = 1400
        this.height = 900
        this.latestSelectedNode = undefined
        this.latestSelectedLink = undefined
        this.contracts = []
        this.sum = 0
        this.force = undefined

        this._nodes = []
        this._links = []
    }

    @action
    public clearFilter() {
        this.searchText = ''
        this.governingLaw = ''
        this.counterparty = ''
        this.language = ''
        this.disputeResolution = ''
        this.contractType = 0
        this.isConfidentiality = false
        this.noConfidentiality = false
        this.isChangeOfControl = false
        this.noChangeOfControl = false
        this.isIndemnity = false
        this.noIndemnity = false
        this.sensitivity = ''
        this.current = false
        this.historical = false
        this.date_sign_start = ''
        this.date_sign_end = ''
        this.termination_start = ''
        this.termination_end = ''
        this.contract_sum_start = ''
        this.contract_sum_end = ''
        this.isOpenEnded = false
        this.noOpenEnded = false
    }

    @action
    public resetSelect() {
        this._nodes.forEach(n => n.selected = false)
        this._links.forEach(r => r.selected = false)
        this.latestSelectedNode = undefined
        this.latestSelectedLink = undefined
    }

    @action
    public selectNode(node: GNode) {
        this.resetSelect()
        node.selected = true
        this.latestSelectedNode = node
        this.latestSelectedLink = undefined
    }

    @action
    public selectLink(link: GLink, sum: number) {
        this.sum = sum
        this.resetSelect()
        link.selected = true
        this.latestSelectedLink = link
        this.latestSelectedNode = undefined
    }

    @action
    public highlightNode = (node: GNode) => {
        node.highlight = true
        this.links.forEach(link => {
            if (link.source.id === node.id || link.target.id === node.id) {
                link.highlight = true
                link.source.highlight = true
                link.target.highlight = true
            }
        })
    }

    @action
    public highlightLink = (link: GLink) => {
        link.highlight = true
        link.target.highlight = true
        link.source.highlight = true
    }

    @action
    public resetHighlight = () => {
        this._nodes.forEach(n => n.highlight = false)
        this._links.forEach(r => r.highlight = false)
    }

    @action
    public addEntity(node: INode, rels: IRelationship[]) {
        if (this.entities.find(item => item.id === node.id)) {
            return
        } else {
            if (this.entities.length >= 5) {
                return
            } else {
                this.entities.push(node)
                if (this.nodesMap[node.id] !== undefined) {
                    return
                }
                const gnode = new GNode(node)
                if (this.entities.length === 1
                    && !this.latestSelectedNode
                    && !this.latestSelectedLink) {
                    gnode.x = gnode.y = gnode.fx = gnode.fy = 0
                }
                this._nodes.push(gnode)
                // 过滤已存在的关系
                const new_links = rels.filter(rel => {
                    const { type, start, end, props } = rel
                    let key = ''
                    if (rel.type === 'Own') {
                        key = `${rel.type}-${start.id}-${end.id}`
                    } else {
                        key = `${type}-${start.id}-${end.id}-${props.id}`
                    }
                    return this.linksMap.get(key) === undefined
                })
                const links = new_links.filter(link => this.nodesMap[link.start.id] && this.nodesMap[link.end.id])
                this._links.push(...links.map(rel => {
                    const { id, props, type, start, end } = rel
                    const link = { id, type, props }
                    const source = this.nodesMap[start.id]
                    const target = this.nodesMap[end.id]
                    return new GLink(link, source, target)
                }))
                this.reSimulation()
            }
        }
    }

    @action
    public removeEntity(id: number) {
        this.removeNode(`${id}`)
    }

    @action
    public removeNode(id: string) {
        const entities = this.entities.filter(item => item.id !== parseInt(id, 10))
        this.entities = entities
        const relatedSingleNodeIds = this.relatedSingleNodeIds(id)
        relatedSingleNodeIds.push(id)
        // const nodes = this._nodes.filter(node => node.id !== id)
        const nodes = this._nodes.filter(node => !relatedSingleNodeIds.includes(node.id))
        this._nodes = nodes
        const links = this._links.filter(link => link.source.id !== id && link.target.id !== id)
        this._links = links
        if (this.latestSelectedNode && this.latestSelectedNode.id === `${id}`) {
            this.latestSelectedNode = undefined
        }
        if (this.entities.length === 0 && this.nodes.length === 0) {
            this._links = []
            this._nodes = []
        }
    }

    public relatedSingleNodeIds(id: string) {
        const relatedLinks = this.links.filter(link => link.source.id === id || link.target.id === id)
        const set = new Set<string>()
        for (const link of relatedLinks) {
            set.add(link.source.id)
            set.add(link.target.id)
        }
        const ids: string[] = []
        for (const filterId of set) {
            const filterLinks = this.links.filter(link => link.source.id === filterId || link.target.id === filterId)
            if (filterLinks.length === 1) {
                ids.push(filterId)
            }
        }
        return ids
    }

    @action
    public expandNode(currNode: GNode, nodes: INode[], rels: IRelationship[]) {
        // 过滤掉已经存在节点
        const newNodes = nodes.filter(node => this.nodesMap[node.id] === undefined)
        this._nodes.push(...newNodes.map(node => new GNode(node)))
        // 过滤掉已存在的关系
        const new_links = rels.filter(rel => {
            const { type, start, end, props } = rel
            let key = ''
            if (rel.type === 'Own') {
                key = `${rel.type}-${start.id}-${end.id}`
            } else {
                key = `${type}-${start.id}-${end.id}-${props.id}`
            }
            return this.linksMap.get(key) === undefined
        })
        const links = new_links.filter(link => this.nodesMap[link.start.id] && this.nodesMap[link.end.id])
        this._links.push(...links.map(rel => {
            const { id, props, type, start, end } = rel
            const link = { id, type, props }
            const source = this.nodesMap[start.id]
            const target = this.nodesMap[end.id]
            return new GLink(link, source, target)
        }))
        this.reSimulation()
    }

    @action
    public resetPanel() {
        this.entities = []
        this._links = []
        this._nodes = []
        this.latestSelectedLink = undefined
        this.latestSelectedNode = undefined
    }

    @action
    public setContracts(contracts: any[]) {
        this.contracts = contracts
    }

    @action
    public setIds(ids: number[]) {
        this.ids = ids
    }

    public reSimulation = (ignoreFixed: boolean = false) => {
        let lasttime = Date.now()
        const nodes = this._nodes.map(n => {
            if (ignoreFixed) {
                return { id: n.id, x: null, y: null, fx: null, fy: null }
            }
            return { id: n.id, x: n.x, y: n.y, fx: n.fx, fy: n.fy }
        })
        const links = this.links.map(l => {
            return { id: l.id, source: l.source.id, target: l.target.id }
        })
        if (this.force) {
            this.force.stop()
        }
        const animate = true
        this.force = forceSimUtil(nodes, links, animate, (positions) => {
            // this.force = forceSimUtil(this._nodes, this.links, animate, (positions) => {
            // tick callback
            // 30帧/秒，避免渲染卡顿
            if (animate &&
                Date.now() - lasttime < 1000 / 30) {
                return
            }
            lasttime = Date.now()
            this.updateNodesPosition(positions, ignoreFixed)
        }, () => {
            // this._nodes.forEach(n => n.isFixed = true)
        })
    }

    @action
    public updateNodesPosition = (
        positions: Array<{ id: string, x: number, y: number }>,
        ignoreFixed: boolean = false,
    ) => {
        const nodes = this._nodes.filter(n => !n.isFixed || ignoreFixed)
        // const nodes = this._nodes
        positions.forEach(n => {
            const node = nodes.find(_n => `${_n.id}` === n.id)
            if (node) {
                node.x = node.fx = n.x
                node.y = node.fy = n.y
                node.setX(node.fx)
                node.setY(node.fy)
            }
        })
    }

    @action
    public changeLayout = (layoutType: string) => {
        if (this.force) {
            this.force.stop()
        }
        if (this.entities.length === 0 && this.nodes.length === 0) {
            return
        }
        this.latestSelectedLink = undefined
        this.latestSelectedNode = undefined
        if (!layoutType || layoutType === 'force') {
            this.reSimulation(true)
            return
        }
        const positions = makeLayoutData(
            this.entities.length > 0 ? `${this.entities[0].id}` : this.nodes[0].id,
            this.nodes,
            this.links,
            layoutType,
        )
        this.updateNodesPosition(positions, true)
    }

    public getSearchForm(): any {
        const searchType = 0
        const contract_sum_start = this.contract_sum_start === '' ? 0 : parseFloat(this.contract_sum_start)
        const contract_sum_end = this.contract_sum_end === '' ? 1000000000000 : parseFloat(this.contract_sum_end)
        const data = {
            searchType: searchType === undefined ? 0 : searchType,
            searchText: '',
            governing_law: toJS(this.governingLaw).trim(),
            dispute_resolution: toJS(this.disputeResolution).trim(),
            counterparty: toJS(this.counterparty).trim(),
            language: toJS(this.language),
            contract_type: toJS(this.contractType),
            confidentiality: toJS(this.confidentiality),
            change_of_control: toJS(this.changeOfControl),
            indemnity: toJS(this.indemnity),
            sensitivity: toJS(this.sensitivity),
            current: toJS(this.current),
            historical: toJS(this.historical),
            date_sign_start: toJS(this.date_sign_start),
            date_sign_end: toJS(this.date_sign_end),
            term_start: toJS(this.termination_start),
            term_end: toJS(this.termination_end),
            open_ended: toJS(this.open_ended),
            contract_sum_start,
            contract_sum_end,
        }
        return data
    }

    @action
    public setLoading(loading: boolean = false) {
        this.loading = loading
    }

    @action
    public setSearchText(text: string) {
        this.searchText = text
    }

    @action
    public setGoverningLaw(governingLaw: string) {
        this.governingLaw = governingLaw
    }

    @action
    public setCounterparty(counterparty: string) {
        this.counterparty = counterparty
    }

    @action
    public setDisputeResolution(disputeResolution: string) {
        this.disputeResolution = disputeResolution
    }

    @action
    public setSensitivity(sensitivity: string) {
        this.sensitivity = sensitivity
    }

    @action
    public setCurrent(current: boolean) {
        this.current = current
    }

    @action
    public setHistorical(historical: boolean) {
        this.historical = historical
    }

    @action
    public setContractType(type: number) {
        this.contractType = type
    }

    @action
    public modifyIsIndemnity(isIndemnity: boolean) {
        this.isIndemnity = isIndemnity
    }

    @action
    public modifyNoIndemnity(noIndemnity: boolean) {
        this.noIndemnity = noIndemnity
    }

    @action
    public modifyIsChangeOfControl(isChangeOfControl: boolean) {
        this.isChangeOfControl = isChangeOfControl
    }

    @action
    public modifyNoChangeOfControl(noChangeOfControl: boolean) {
        this.noChangeOfControl = noChangeOfControl
    }

    @action
    public modifyIsConfidentiality(isConfidentiality: boolean) {
        this.isConfidentiality = isConfidentiality
    }

    @action
    public modifyNoConfidentiality(noConfidentiality: boolean) {
        this.noConfidentiality = noConfidentiality
    }

    @action
    public modifyIsOpenEnded(isOpenEnded: boolean) {
        this.isOpenEnded = isOpenEnded
    }

    @action
    public modifyNoOpenEnded(noOpenEnded: boolean) {
        this.noOpenEnded = noOpenEnded
    }

    @action
    public setLanguage(language: string) {
        this.language = language
    }

    @action
    public setSignDateRange(dateStrings: [string, string]) {
        this.date_sign_start = dateStrings[0]
        this.date_sign_end = dateStrings[1]
    }

    @action
    public setTermDateRange(dateStrings: [string, string]) {
        this.termination_start = dateStrings[0]
        this.termination_end = dateStrings[1]
    }

    @action
    public setSignDateStart(value: string) {
        this.date_sign_start = value
    }

    @action
    public setSignDateEnd(value: string) {
        this.date_sign_end = value
    }

    @action
    public setTermStartDate(value: string) {
        this.termination_start = value
    }

    @action
    public setTermEndDate(value: string) {
        this.termination_end = value
    }

    @action
    public setContractSumStart(start: string) {
        this.contract_sum_start = start
    }

    @action
    public setContractSumEnd(end: string) {
        this.contract_sum_end = end
    }

    @action
    public setMatchEntitys(companys: ICompany[]) {
        this.matchEntitys = companys
    }
}

export default GraphStore
